import axios, { AxiosRequestConfig } from 'axios'

import * as Sentry from '@sentry/vue'

import { ERROR_TYPES, toApiError } from './shared/api/apiError'

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL as string,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-CliniShift-Api-Version': '2024-08-01',
  },
})

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const promise = axiosInstance({
    ...config,
    ...options,
  }).then(({ data }) => data)

  return promise
}

let isLoggingOut = false
let isRedirectingPasswordExpired = false

interface InterceptorOptions {
  onUnauthorized: (config: { skipAuthRedirect: boolean }) => void
  onPasswordExpired: () => void
}

export const setupInterceptors = ({
  onUnauthorized,
  onPasswordExpired,
}: InterceptorOptions) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (err: unknown) => {
      if (!axios.isAxiosError(err)) {
        Sentry.captureException(err)

        return Promise.reject(err)
      }

      const apiError = toApiError(err)

      // TODO: Should 419 also log the user out? This is typically used for CSRF token expiration which can be resolved by refreshing the page, but it can also indicate an unauthenticated session.
      if (apiError.detail.type === ERROR_TYPES.UNAUTHORIZED && !isLoggingOut) {
        isLoggingOut = true

        onUnauthorized({
          skipAuthRedirect: !!err.config?.skipAuthRedirect,
        })

        setTimeout(() => {
          isLoggingOut = false
        }, 1000)
      }

      if (
        apiError.detail.type === ERROR_TYPES.FORBIDDEN &&
        apiError.detail.passwordExpired &&
        !isRedirectingPasswordExpired
      ) {
        // If the error indicates the password is expired, redirect to the settings page with a specific hash to show the password expired message
        isRedirectingPasswordExpired = true

        onPasswordExpired()

        setTimeout(() => {
          isRedirectingPasswordExpired = false
        }, 1000)
      }

      return Promise.reject(apiError)
    },
  )
}

export function resetInterceptors() {
  resetLogoutState()
  resetPasswordExpiredRedirectState()

  axiosInstance.interceptors.response.clear()
}

export function resetLogoutState() {
  isLoggingOut = false
}

export function resetPasswordExpiredRedirectState() {
  isRedirectingPasswordExpired = false
}

export default axiosInstance
